/**
 * Contact list URL-owned filter/pagination state (task 12.1, design.md §3 "State
 * restoration contract" point 1: "Filters, sort..., `page`, and `size` are URL search
 * parameters and are the source of truth. Invalid values are normalized with `replace`,
 * not added to history.").
 *
 * `q` is intentionally NOT part of the URL-owned state. Task 12.4 says explicitly: "do
 * not put email/name in URL, session storage, logs, or generic cache keys". The
 * backend's `EmailFragment` searches `email ILIKE`, `nombre ILIKE`, and `empresa ILIKE`
 * — all PII. So `q` stays in component-local state, triggered by explicit submit, and
 * is never persisted in the URL.
 */

const KNOWN_ESTADO_VALUES = new Set(["Activo", "Suprimido", "EnNegociacion"]);

const KNOWN_NIVEL_VALUES = new Set(["CLevel", "Director", "Gerente", "Coordinador", "Otro"]);

const MIN_SIZE = 1;
const MAX_SIZE = 50;
const DEFAULT_SIZE = 20;
const DEFAULT_PAGE = 1;

export const CONTACT_LIST_ALLOWED_KEYS = ["estado", "nivelJerarquico", "industria", "showId", "page", "size"] as const;

export interface ContactListParams {
  estado?: string;
  nivelJerarquico?: string;
  industria?: string;
  /** issue #274: free-form Show id (a GUID) -- no closed vocabulary, same treatment as industria. */
  showId?: string;
  page: number;
  size: number;
}

export const DEFAULT_CONTACT_LIST_PARAMS: ContactListParams = {
  estado: undefined,
  nivelJerarquico: undefined,
  industria: undefined,
  showId: undefined,
  page: DEFAULT_PAGE,
  size: DEFAULT_SIZE,
};

export interface NormalizedContactListParams {
  params: ContactListParams;
  normalized: URLSearchParams;
  changed: boolean;
}

function normalizePage(raw: string | null): { value: number; changed: boolean } {
  if (raw === null) {
    return { value: DEFAULT_PAGE, changed: false };
  }
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 1) {
    return { value: DEFAULT_PAGE, changed: true };
  }
  return { value: parsed, changed: String(parsed) !== raw };
}

function normalizeSize(raw: string | null): { value: number; changed: boolean } {
  if (raw === null) {
    return { value: DEFAULT_SIZE, changed: false };
  }
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < MIN_SIZE || parsed > MAX_SIZE) {
    return { value: DEFAULT_SIZE, changed: true };
  }
  return { value: parsed, changed: String(parsed) !== raw };
}

function normalizeClosedVocabulary(
  raw: string | null,
  known: Set<string>,
): { value: string | undefined; changed: boolean } {
  if (raw === null || raw === "") {
    return { value: undefined, changed: false };
  }
  if (!known.has(raw)) {
    return { value: undefined, changed: true };
  }
  return { value: raw, changed: false };
}

function normalizeFreeText(raw: string | null): { value: string | undefined; changed: boolean } {
  if (raw === null || raw === "") {
    return { value: undefined, changed: false };
  }
  return { value: raw, changed: false };
}

export function normalizeContactListSearchParams(searchParams: URLSearchParams): NormalizedContactListParams {
  const page = normalizePage(searchParams.get("page"));
  const size = normalizeSize(searchParams.get("size"));
  const estado = normalizeClosedVocabulary(searchParams.get("estado"), KNOWN_ESTADO_VALUES);
  const nivelJerarquico = normalizeClosedVocabulary(searchParams.get("nivelJerarquico"), KNOWN_NIVEL_VALUES);
  const industria = normalizeFreeText(searchParams.get("industria"));
  const showId = normalizeFreeText(searchParams.get("showId"));

  const normalized = new URLSearchParams();
  if (estado.value !== undefined) {
    normalized.set("estado", estado.value);
  }
  if (nivelJerarquico.value !== undefined) {
    normalized.set("nivelJerarquico", nivelJerarquico.value);
  }
  if (industria.value !== undefined) {
    normalized.set("industria", industria.value);
  }
  if (showId.value !== undefined) {
    normalized.set("showId", showId.value);
  }
  normalized.set("page", String(page.value));
  normalized.set("size", String(size.value));

  return {
    params: {
      estado: estado.value,
      nivelJerarquico: nivelJerarquico.value,
      industria: industria.value,
      showId: showId.value,
      page: page.value,
      size: size.value,
    },
    normalized,
    changed:
      page.changed || size.changed || estado.changed || nivelJerarquico.changed || industria.changed || showId.changed,
  };
}

export function buildContactListSearchParams(params: ContactListParams): URLSearchParams {
  const search = new URLSearchParams();
  if (params.estado) {
    search.set("estado", params.estado);
  }
  if (params.nivelJerarquico) {
    search.set("nivelJerarquico", params.nivelJerarquico);
  }
  if (params.industria) {
    search.set("industria", params.industria);
  }
  if (params.showId) {
    search.set("showId", params.showId);
  }
  search.set("page", String(params.page));
  search.set("size", String(params.size));
  return search;
}
